/**
 * useProvisioning Hook
 *
 * Manages schema and container provisioning with polling for progress updates.
 *
 * @module features/tenants/hooks/useProvisioning
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { ProvisioningStatus, ContainerProvisioningProgress } from '../types';
import {
  startProvisioning as startProvisioningApi,
  fetchProvisioningStatus,
  provisionSchema as provisionSchemaApi,
  archiveSchema as archiveSchemaApi,
} from '../api/tenantApi';
import {
  provisionContainers,
  fetchContainerProvisioningStatus,
  fetchContainerStatus,
  resetProvisioningStatus,
} from '../api/containerApi';

/**
 * Auth fetch function type.
 */
type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

/**
 * Callbacks for refreshing data after provisioning completes.
 */
export interface ProvisioningCallbacks {
  onRefreshTenants: () => Promise<void>;
  onRefreshStats: () => void;
  onFetchContainerStatus?: (tenantId: string) => Promise<unknown>;
}

/**
 * Return type for useProvisioning hook.
 */
export interface UseProvisioningResult {
  /** Schema/tenant provisioning status by tenant ID */
  provisioningStatus: Record<string, ProvisioningStatus>;
  /** Container provisioning progress by tenant ID */
  containerProgress: Record<string, ContainerProvisioningProgress>;
  /** Tenant ID currently provisioning schema */
  isProvisioningSchema: string | null;
  /** Tenant ID currently archiving schema */
  isArchivingSchema: string | null;
  /** Tenant ID currently provisioning containers */
  isProvisioningContainer: string | null;

  /** Start tenant provisioning (triggers polling) */
  startProvisioning: (tenantId: string) => Promise<void>;
  /** Provision schema for a tenant */
  provisionSchema: (tenantId: string) => Promise<void>;
  /** Archive schema for a tenant */
  archiveSchema: (tenantId: string) => Promise<void>;
  /** Provision containers for a tenant (triggers polling) */
  provisionContainer: (tenantId: string) => Promise<void>;
  /** Reset stuck provisioning status */
  resetProvisioning: (tenantId: string) => Promise<void>;
  /** Check provisioning status for tenants in 'provisioning' state */
  checkProvisioningTenants: (tenantIds: string[]) => void;
  /** Check container provisioning status for container-isolated tenants in 'provisioning' state */
  checkContainerProvisioningTenants: (tenantIds: string[]) => void;
}

/**
 * Hook for managing schema and container provisioning.
 */
export const useProvisioning = (
  authFetch: AuthFetch,
  callbacks: ProvisioningCallbacks
): UseProvisioningResult => {
  // Schema/tenant provisioning status
  const [provisioningStatus, setProvisioningStatus] = useState<Record<string, ProvisioningStatus>>({});
  const [isProvisioningSchema, setIsProvisioningSchema] = useState<string | null>(null);
  const [isArchivingSchema, setIsArchivingSchema] = useState<string | null>(null);

  // Container provisioning progress
  const [containerProgress, setContainerProgress] = useState<Record<string, ContainerProvisioningProgress>>({});
  const [isProvisioningContainer, setIsProvisioningContainer] = useState<string | null>(null);

  // Track active polls to prevent duplicates
  const activePolls = useRef<Set<string>>(new Set());

  // Cleanup active polls on unmount
  useEffect(() => {
    return () => { activePolls.current.clear(); };
  }, []);

  /**
   * Poll tenant provisioning status until complete.
   */
  const pollProvisioningStatus = useCallback((tenantId: string) => {
    if (activePolls.current.has(`prov-${tenantId}`)) return;
    activePolls.current.add(`prov-${tenantId}`);

    const checkStatus = async () => {
      try {
        const data = await fetchProvisioningStatus(authFetch, tenantId);
        setProvisioningStatus(prev => ({ ...prev, [tenantId]: data }));

        if (data.status === 'in_progress') {
          setTimeout(checkStatus, 1000);
        } else {
          activePolls.current.delete(`prov-${tenantId}`);
          if (data.status === 'completed') {
            await callbacks.onRefreshTenants();
            callbacks.onRefreshStats();
          }
        }
      } catch {
        activePolls.current.delete(`prov-${tenantId}`);
      }
    };
    checkStatus();
  }, [authFetch, callbacks]);

  /**
   * Start tenant provisioning.
   */
  const startProvisioning = useCallback(async (tenantId: string) => {
    await startProvisioningApi(authFetch, tenantId);
    pollProvisioningStatus(tenantId);
  }, [authFetch, pollProvisioningStatus]);

  /**
   * Provision schema for a tenant.
   */
  const provisionSchema = useCallback(async (tenantId: string) => {
    setIsProvisioningSchema(tenantId);
    try {
      await provisionSchemaApi(authFetch, tenantId);
      await callbacks.onRefreshTenants();
      callbacks.onRefreshStats();
    } finally {
      setIsProvisioningSchema(null);
    }
  }, [authFetch, callbacks]);

  /**
   * Archive schema for a tenant.
   */
  const archiveSchema = useCallback(async (tenantId: string) => {
    setIsArchivingSchema(tenantId);
    try {
      await archiveSchemaApi(authFetch, tenantId);
      await callbacks.onRefreshTenants();
      callbacks.onRefreshStats();
    } finally {
      setIsArchivingSchema(null);
    }
  }, [authFetch, callbacks]);

  /**
   * Poll container provisioning status until complete.
   * Retries on transient errors before giving up.
   *
   * NOTE: 'pending' is the INITIAL state before the background task starts,
   * NOT a terminal state. Only 'completed' and 'failed' are terminal.
   * A max poll duration prevents infinite polling if something stalls.
   */
  const pollContainerProvisioning = useCallback((tenantId: string) => {
    if (activePolls.current.has(`cont-${tenantId}`)) return;
    activePolls.current.add(`cont-${tenantId}`);

    let consecutiveErrors = 0;
    const MAX_POLL_ERRORS = 5;
    const MAX_POLL_DURATION_MS = 5 * 60 * 1000; // 5 minutes max polling
    const pollStartTime = Date.now();

    const checkStatus = async () => {
      // Safety: stop polling after max duration
      if (Date.now() - pollStartTime > MAX_POLL_DURATION_MS) {
        console.warn(`Provisioning poll timeout for ${tenantId} after ${MAX_POLL_DURATION_MS / 1000}s`);
        activePolls.current.delete(`cont-${tenantId}`);
        setIsProvisioningContainer(null);
        setContainerProgress(prev => ({
          ...prev,
          [tenantId]: {
            tenant_id: tenantId,
            tenant_slug: '',
            status: 'failed',
            current_step: 0,
            total_steps: 6,
            progress_pct: 0,
            message: 'Provisioning status polling timed out. Check server logs or reset provisioning.',
            started_at: new Date().toISOString(),
            completed_at: new Date().toISOString(),
            error: 'Polling timeout',
          },
        }));
        return;
      }

      try {
        const data = await fetchContainerProvisioningStatus(authFetch, tenantId);
        consecutiveErrors = 0; // Reset on success
        setContainerProgress(prev => ({ ...prev, [tenantId]: data }));

        // Only 'failed' is an immediate terminal state.
        // 'completed' means backend provisioning is done, but we still need
        // to wait for containers to become healthy before showing final state.
        if (data.status === 'failed') {
          activePolls.current.delete(`cont-${tenantId}`);
          setIsProvisioningContainer(null);
          callbacks.onFetchContainerStatus?.(tenantId);
        } else if (data.status === 'completed') {
          // Backend provisioning done — now wait for containers to be healthy.
          // Show an extended progress state so the UI doesn't flash errors.
          setContainerProgress(prev => ({
            ...prev,
            [tenantId]: {
              ...data,
              status: 'stabilizing',
              message: 'Waiting for containers to become healthy...',
            },
          }));
          // Poll container status until healthy
          const waitForHealthy = async (attempt = 0) => {
            const MAX_WAIT_ATTEMPTS = 40; // ~2 min at 3s intervals
            if (attempt >= MAX_WAIT_ATTEMPTS) {
              // Timeout — show as completed anyway and let user refresh
              activePolls.current.delete(`cont-${tenantId}`);
              setIsProvisioningContainer(null);
              setContainerProgress(prev => ({ ...prev, [tenantId]: { ...data, status: 'completed' } }));
              await callbacks.onRefreshTenants();
              callbacks.onRefreshStats();
              callbacks.onFetchContainerStatus?.(tenantId);
              return;
            }
            try {
              const cs = await fetchContainerStatus(authFetch, tenantId);
              if (cs && cs.status === 'running') {
                const containers = cs.containers || {};
                const allHealthy = Object.values(containers).every(c => c.running && c.healthy);
                if (allHealthy) {
                  // Containers are truly healthy — complete!
                  activePolls.current.delete(`cont-${tenantId}`);
                  setIsProvisioningContainer(null);
                  setContainerProgress(prev => ({ ...prev, [tenantId]: { ...data, status: 'completed' } }));
                  await callbacks.onRefreshTenants();
                  callbacks.onRefreshStats();
                  callbacks.onFetchContainerStatus?.(tenantId);
                  return;
                }
              }
            } catch {
              // Ignore fetch errors, keep polling
            }
            setTimeout(() => waitForHealthy(attempt + 1), 3000);
          };
          waitForHealthy();
        } else {
          // Active provisioning or waiting to start - keep polling
          const interval = data.status === 'pending' ? 1000 : 1500;
          setTimeout(checkStatus, interval);
        }
      } catch (err) {
        consecutiveErrors++;
        console.error(`Poll error ${consecutiveErrors}/${MAX_POLL_ERRORS} for ${tenantId}:`, err);

        if (consecutiveErrors < MAX_POLL_ERRORS) {
          // Retry with backoff
          setTimeout(checkStatus, 2000 * consecutiveErrors);
        } else {
          // Give up - mark as failed so UI shows error state
          activePolls.current.delete(`cont-${tenantId}`);
          setIsProvisioningContainer(null);
          setContainerProgress(prev => ({
            ...prev,
            [tenantId]: {
              tenant_id: tenantId,
              tenant_slug: '',
              status: 'failed',
              current_step: 0,
              total_steps: 6,
              progress_pct: 0,
              message: `Lost connection to provisioning status: ${err instanceof Error ? err.message : 'Unknown error'}`,
              started_at: new Date().toISOString(),
              completed_at: null,
              error: err instanceof Error ? err.message : 'Poll failed',
            },
          }));
        }
      }
    };
    checkStatus();
  }, [authFetch, callbacks]);

  /**
   * Provision containers for a tenant.
   * Sets immediate synthetic progress so UI shows feedback instantly.
   */
  const provisionContainer = useCallback(async (tenantId: string) => {
    setIsProvisioningContainer(tenantId);

    // Set immediate synthetic progress so the UI shows feedback right away
    setContainerProgress(prev => ({
      ...prev,
      [tenantId]: {
        tenant_id: tenantId,
        tenant_slug: '',
        status: 'pending',
        current_step: 0,
        total_steps: 6,
        progress_pct: 0,
        message: 'Starting container provisioning...',
        started_at: new Date().toISOString(),
        completed_at: null,
        error: null,
      },
    }));

    try {
      await provisionContainers(authFetch, tenantId);
      // Refresh tenant list so status updates to 'provisioning' in the table
      await callbacks.onRefreshTenants();
      callbacks.onRefreshStats();
      pollContainerProvisioning(tenantId);
    } catch (err) {
      console.error('Failed to start container provisioning:', err);
      // Clear provisioning flag and show error in progress
      setIsProvisioningContainer(null);
      setContainerProgress(prev => ({
        ...prev,
        [tenantId]: {
          tenant_id: tenantId,
          tenant_slug: '',
          status: 'failed',
          current_step: 0,
          total_steps: 6,
          progress_pct: 0,
          message: `Failed to start provisioning: ${err instanceof Error ? err.message : 'Unknown error'}`,
          started_at: new Date().toISOString(),
          completed_at: new Date().toISOString(),
          error: err instanceof Error ? err.message : 'Failed to start',
        },
      }));
      throw err; // Re-throw so callers can handle too
    }
  }, [authFetch, callbacks, pollContainerProvisioning]);

  /**
   * Reset stuck provisioning status.
   */
  const resetProvisioning = useCallback(async (tenantId: string) => {
    try {
      await resetProvisioningStatus(authFetch, tenantId);
      // Clear local provisioning status
      setProvisioningStatus(prev => {
        const updated = { ...prev };
        delete updated[tenantId];
        return updated;
      });
      setContainerProgress(prev => {
        const updated = { ...prev };
        delete updated[tenantId];
        return updated;
      });
      // Refresh tenants to get updated status
      await callbacks.onRefreshTenants();
      callbacks.onRefreshStats();
    } catch (error) {
      console.error('Failed to reset provisioning:', error);
      throw error;
    }
  }, [authFetch, callbacks]);

  /**
   * Check provisioning status for tenants in 'provisioning' state.
   * Use for non-container (row/schema) tenants.
   */
  const checkProvisioningTenants = useCallback((tenantIds: string[]) => {
    tenantIds.forEach(id => {
      if (!provisioningStatus[id]) {
        pollProvisioningStatus(id);
      }
    });
  }, [provisioningStatus, pollProvisioningStatus]);

  /**
   * Check container provisioning status for container-isolated tenants.
   * Starts polling the container provisioning endpoint for each tenant.
   */
  const checkContainerProvisioningTenants = useCallback((tenantIds: string[]) => {
    tenantIds.forEach(id => {
      if (!containerProgress[id] || !['completed', 'failed'].includes(containerProgress[id].status)) {
        pollContainerProvisioning(id);
      }
    });
  }, [containerProgress, pollContainerProvisioning]);

  return {
    provisioningStatus,
    containerProgress,
    isProvisioningSchema,
    isArchivingSchema,
    isProvisioningContainer,
    startProvisioning,
    provisionSchema,
    archiveSchema,
    provisionContainer,
    resetProvisioning,
    checkProvisioningTenants,
    checkContainerProvisioningTenants,
  };
};
